import { Button, Modal, Form, Row, Col, Select, Input, message } from "antd";
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleUpdateUser } from "../../../../api/users";
import { handleFetchUnits } from "../../../../api/units";
import { handleFetchBusinesses } from "../../../../api/businesses";

const { Option } = Select;

export default function UpdateUser({ isVisible, setVisible, selected }: any) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { mutate, isPending } = useMutation({
    mutationFn: handleUpdateUser,
    onSuccess: () => {
      message.success("Updated Successfully");
      form.resetFields();
      setVisible(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: () => {
      message.error("Something went wrong");
    },
  });

  const { data: units } = useQuery({
    queryKey: ["units"],
    queryFn: () => handleFetchUnits({}),
  });

  const { data: businesses } = useQuery({
    queryKey: ["campanies"],
    queryFn: () => handleFetchBusinesses({ page: 1, limit: 40 }),
  });

  const onSubmit = async () => {
    const values = await form.getFieldsValue();
    const data = { ...values, accessLevel: Number(values?.accessLevel) };
    await mutate({ _id: selected?._id, data });
  };

  const clearForm = async () => {
    form.resetFields();
  };

  const unitOptions = units?.records?.map((unit: any, index: number) => (
    <Option key={index} value={unit?._id}>
      {unit?.name}
    </Option>
  ));

  const companyOptions = businesses?.records?.map(
    (item: any, index: number) => (
      <Option key={index} value={item?.companyNo}>
        {` Co. ${item?.companyNo} - ${item?.name}`}
      </Option>
    )
  );

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({ name: selected?.name });
    form.setFieldsValue({ email: selected?.email });
    form.setFieldsValue({ mobile: selected?.mobile });
    form.setFieldsValue({ empId: selected?.empId });
    form.setFieldsValue({ businessUnitRef: selected?.businessUnitRef });
    form.setFieldsValue({ legalEntity: selected?.legalEntity });
    form.setFieldsValue({ companyNo: selected?.companyNo });
    form.setFieldsValue({ accessLevel: selected?.accessLevel });
  }, [selected]);

  return (
    <Modal
      open={isVisible}
      title="Update User"
      onCancel={() => setVisible(false)}
      footer={[
        <Button danger key="back" onClick={clearForm}>
          Clear
        </Button>,
        <Button
          key="submit"
          form="updateUser"
          htmlType="submit"
          type="primary"
          onSubmit={onSubmit}
          loading={isPending}
          danger
        >
          Update
        </Button>,
      ]}
    >
      <Form
        id="updateUser"
        form={form}
        name="normal_login"
        className="login-form"
        onFinish={onSubmit}
        layout="vertical"
      >
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="empId"
              label="Employee KJL ID"
              rules={[
                {
                  required: false,
                  message: "Please enter employee ID",
                },
              ]}
            >
              <Input type="text" placeholder="Employee ID" />
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="name"
              label="Full Name"
              rules={[
                {
                  required: true,
                  message: "Please enter full name",
                },
              ]}
            >
              <Input type="text" placeholder="Full Name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  required: false,
                  message: "Please enter email",
                },
              ]}
            >
              <Input type="email" placeholder="Email" />
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="mobile"
              label="Mobile"
              rules={[
                {
                  required: false,
                  message: "Please enter mobile",
                },
              ]}
            >
              <Input type="text" placeholder="Mobile" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="cluster"
              label={"Cluster"}
              rules={[
                {
                  required: false,
                  message: "Please enter cluster",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select Cluster"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={"Karimjee Capital"}>Karimjee Capital</Option>
                <Option value={"Karimjee Energy"}>Karimjee Energy</Option>
                <Option value={"Karimjee Family"}>Karimjee Family</Option>
                <Option value={"Karimjee Finance"}>Karimjee Finance</Option>
                <Option value={"Karimjee Group Function"}>
                  Karimjee Group Function
                </Option>
                <Option value={"Karimjee Hospitality"}>
                  Karimjee Hospitality
                </Option>
                <Option value={"Karimjee Mobility"}>Karimjee Mobility</Option>
                <Option value={"Karimjee Real Estate"}>
                  Karimjee Real Estate
                </Option>
                <Option value={"Karimjee Value Chain"}>
                  Karimjee Value Chain
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="legalEntity"
              label={"Legal Entity"}
              rules={[
                {
                  required: false,
                  message: "Please enter legal entity",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Legal Entity"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={"Toyota Tanzania Ltd"}>
                  Toyota Tanzania Ltd
                </Option>
                <Option value={"Karimjee Jivanjee Ltd"}>
                  Karimjee Jivanjee Ltd
                </Option>
                <Option value={"Karimjee Value Chain Ltd"}>
                  Karimjee Value Chain Ltd
                </Option>
                <Option value={"Salute Finance Ltd"}>Salute Finance Ltd</Option>
                <Option value={"Salute Insurance Ltd"}>
                  Salute Insurance Ltd
                </Option>
                <Option value={"Sunbeam Auto Ltd"}>Sunbeam Auto Ltd</Option>
                <Option value={"City Motors Ltd"}>City Motors Ltd</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="companyNo"
              label="Autoline Company No."
              rules={[
                {
                  required: true,
                  message: `Please enter company number`,
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Autoline Company No."
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {companyOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="businessSegment"
              label={"Business Segment"}
              rules={[
                {
                  required: false,
                  message: "Please enter business segment",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Business Segment"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={"Toyota"}>Toyota</Option>
                <Option value={"Value Chain"}>Value Chain</Option>
                <Option value={"KJ Group"}>KJ Group</Option>
                <Option value={"KJ Family"}>KJ Family</Option>
                <Option value={"Mass Mobility"}>Mass Mobility</Option>
                <Option value={"Real Estate"}>Real Estate</Option>
                <Option value={"Various"}>Various</Option>
                <Option value={"Hospitality"}>Hospitality</Option>
                <Option value={"Finance"}>Finance</Option>
                <Option value={"Energy"}>Energy</Option>
                <Option value={"Group Function"}>Group Function</Option>
                <Option value={"Karimjee Capital"}>Karimjee Capital</Option>
                <Option value={"Non-Toyota"}>Non-Toyota</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="businessDivision"
              label={"Business Division"}
              rules={[
                {
                  required: false,
                  message: "Please enter business division",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Business Division"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={"Ancillary Products"}>Ancillary Products</Option>
                <Option value={"Automotive 2nd Channel"}>
                  Automotive 2nd Channel
                </Option>
                <Option value={"Family Office Functions"}>
                  Family Office Functions
                </Option>
                <Option value={"Financial Services"}>Financial Services</Option>
                <Option value={"Group Functions"}>Group Functions</Option>
                <Option value={"Hospitality"}>Hospitality</Option>
                <Option value={"Mass Mobility"}>Mass Mobility</Option>
                <Option value={"Real Estate"}>Real Estate</Option>
                <Option value={"Toyota Tanzania - Branches"}>
                  Toyota Tanzania - Branches
                </Option>
                <Option value={"Toyota Tanzania -"}>Toyota Tanzania -</Option>
                <Option value={"Various"}>Various</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="businessUnitRef"
              label="Business Unit"
              rules={[
                {
                  required: true,
                  message: `Please enter business unit`,
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Business Unit"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {unitOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="department"
              label="Department"
              rules={[
                {
                  required: false,
                  message: `Please enter department`,
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Department"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={"Body and Paint Shop"}>
                  Body and Paint Shop
                </Option>
                <Option value={"Business Improvement"}>
                  Business Improvement
                </Option>
                <Option value={"Business Operations"}>
                  Business Operations
                </Option>
                <Option value={"Customer Experience"}>
                  Customer Experience
                </Option>
                <Option value={"Drivers"}>Drivers</Option>
                <Option value={"EXCO"}>EXCO</Option>
                <Option value={"Facilities Management"}>
                  Facilities Management
                </Option>
                <Option value={"Family Residence"}>Family Residence</Option>
                <Option value={"Finance"}>Finance</Option>
                <Option value={"Finance & Accounts"}>Finance & Accounts</Option>
                <Option value={"Financial Services"}>Financial Services</Option>
                <Option value={"Food and Beverage"}>Food and Beverage</Option>
                <Option value={"Group Functions"}>Group Functions</Option>
                <Option value={"Human Resources"}>Human Resources</Option>
                <Option value={"Information Technology"}>
                  Information Technology
                </Option>
                <Option value={"Innovation Fund"}>Innovation Fund</Option>
                <Option value={"Internal Audit & Sys. Mngt"}>
                  Internal Audit & Sys. Mngt
                </Option>
                <Option value={"Karimjee Foundation"}>
                  Karimjee Foundation
                </Option>
                <Option value={"Marketing"}>Marketing</Option>
                <Option value={"Mass Mobility"}>Mass Mobility</Option>
                <Option value={"Operations - Aftersales"}>
                  Operations - Aftersales
                </Option>
                <Option value={"Operations - Other"}>Operations - Other</Option>
                <Option value={"Parts"}>Parts</Option>
                <Option value={"Sales"}>Sales</Option>
                <Option value={"Security"}>Security</Option>
                <Option value={"Service"}>Service</Option>
                <Option value={"THE OVERHANG"}>THE OVERHANG</Option>
                <Option value={"Toyota Tanzania - Branches"}>
                  Toyota Tanzania - Branches
                </Option>
                <Option value={"Various"}>Various</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Form.Item
              name="accessLevel"
              label={"Access Level"}
              rules={[
                {
                  required: false,
                  message: "Please select access level",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Access Level"
                optionFilterProp="children"
                filterOption={(input: any, option: any) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={0}>Level 0</Option>
                <Option value={1}>Level 1</Option>
                <Option value={2}>Level 2</Option>
                <Option value={3}>Level 3</Option>
                <Option value={4}>Level 4</Option>
                <Option value={5}>Level 5</Option>
                <Option value={9}>Level 9</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
